// Окно «Новая задача» → POST /api/tasks.
import { useEffect, useId, useState } from 'react'
import { api, errorMessage, uploadFile } from '../api.ts'
import { useBusy } from '../lib/hooks.ts'
import { PRIORITY_LABELS } from '../lib/format.ts'
import { useLive } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import { SAFETY_MODE_LABELS, type AttachmentInfo, type Priority, type SafetyMode, type Task } from '../types.ts'
import { AgentSelect } from './AgentSelect.tsx'
import { DropOverlay, FilePicker, MAX_FILE_BYTES, PendingFiles, useFileDrop } from './Attachments.tsx'
import { DirectionChip } from './chips.tsx'
import { Modal } from './Layer.tsx'
import { Icon } from './Icon.tsx'
import { Button, Field, Kbd, Spinner } from './ui.tsx'

const PRIORITIES: Priority[] = ['low', 'normal', 'high', 'urgent']

/** Ссылка на задачу Битрикс24: …/tasks/task/view/131134/ (и в группах …/workgroups/group/N/tasks/task/view/ID/). */
// завершающий «/» обязателен: пока номер набирают руками, недописанная ссылка не подтягивает чужую задачу
const B24_TASK = /https?:\/\/[^\s]+?\/task\/view\/\d+\//i
const onlyLink = (s: string): boolean => Boolean(s.trim()) && !s.replace(/https?:\/\/\S+/g, '').trim()

interface B24Preview { id: number; title: string; status: string; responsible: string; deadline: string | null; url: string }
type B24State = { state: 'idle' } | { state: 'loading'; url: string } | { state: 'ok'; url: string; task: B24Preview } | { state: 'error'; url: string; error: string }

/** Ссылка на задачу Битрикс24 в заголовке или описании → что это за задача; в заголовке только ссылка — подставить название. */
function useBitrixPreview(title: string, description: string, setTitle: (f: (s: string) => string) => void, setDescription: (f: (s: string) => string) => void): B24State {
  const [s, setS] = useState<B24State>({ state: 'idle' })
  const link = `${title}\n${description}`.match(B24_TASK)?.[0] ?? null
  useEffect(() => {
    if (!link) return setS({ state: 'idle' })
    const ctl = new AbortController()
    setS({ state: 'loading', url: link })
    const timer = window.setTimeout(() => {
      api
        .get<{ configured: boolean; task: B24Preview | null; error?: string }>(`/api/bitrix/preview?url=${encodeURIComponent(link)}`, { signal: ctl.signal })
        .then((r) => {
          if (!r.configured) return setS({ state: 'error', url: link, error: 'Битрикс24 не подключён к OMA' })
          if (!r.task) return setS({ state: 'error', url: link, error: r.error ?? 'задача не найдена или нет доступа' })
          setS({ state: 'ok', url: link, task: r.task })
          setTitle((cur) => (onlyLink(cur) ? r.task!.title : cur))
          setDescription((cur) => (cur.includes(link) ? cur : `${link}\n\n${cur}`.trim()))
        })
        .catch((e) => {
          if (!ctl.signal.aborted) setS({ state: 'error', url: link, error: errorMessage(e) })
        })
    }, 350)
    return () => {
      window.clearTimeout(timer)
      ctl.abort()
    }
  }, [link])
  return s
}

function BitrixCard({ s }: { s: B24State }) {
  if (s.state === 'idle') return null
  return (
    <div className="-mt-1.5 flex min-w-0 items-start gap-2 rounded-card border border-line bg-surface-2 px-3 py-2 text-[12.5px] [overflow-wrap:anywhere]" aria-live="polite">
      {s.state === 'loading' ? (
        <>
          <Spinner size={14} className="shrink-0" />
          <span className="min-w-0 text-muted">Читаю задачу Битрикс24…</span>
        </>
      ) : s.state === 'error' ? (
        <>
          <Icon name="alertCircle" size={14} className="mt-0.5 shrink-0 text-warn" />
          <span className="min-w-0 text-muted">
            Битрикс24: {s.error}. Задачу можно создать — агент попробует прочитать её сам.
          </span>
        </>
      ) : (
        <>
          <Icon name="link" size={14} className="mt-0.5 shrink-0 text-subtle" />
          <div className="min-w-0">
            <div className="font-medium text-fg">
              Битрикс24 #{s.task.id} «{s.task.title}»
            </div>
            <div className="text-muted">
              {s.task.status} · ответственный {s.task.responsible}
              {s.task.deadline ? ` · срок ${s.task.deadline}` : ''}
            </div>
            <div className="mt-0.5 text-subtle">При создании OMA приложит задачу целиком и всю переписку по ней — агент прочтёт их до начала работы.</div>
          </div>
        </>
      )}
    </div>
  )
}

export function NewTaskModal({
  onClose,
  onCreated,
  initialDirections = [],
}: {
  onClose: () => void
  onCreated?: (t: Task) => void
  initialDirections?: string[]
}) {
  const directions = useLive((s) => s.directions)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<Priority>('normal')
  const [dirs, setDirs] = useState<string[]>(initialDirections)
  const [assignee, setAssignee] = useState('')
  const [mode, setMode] = useState<SafetyMode | ''>('')
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<(number | undefined)[]>([])
  const addFiles = (fs: File[]) => {
    const big = fs.filter((f) => f.size > MAX_FILE_BYTES)
    if (big.length) toast(`Больше 100 МБ, не прикреплено: ${big.map((f) => f.name).join(', ')}`, 'error')
    const ok = fs.filter((f) => f.size <= MAX_FILE_BYTES && f.size > 0)
    if (ok.length) setFiles((cur) => [...cur, ...ok])
  }
  const dragging = useFileDrop(addFiles)
  const [acceptance, setAcceptance] = useState('')
  const [showAcceptance, setShowAcceptance] = useState(false)
  const [busy, run] = useBusy()
  const ids = { title: useId(), desc: useId(), prio: useId(), assignee: useId(), acc: useId(), mode: useId() }
  const b24 = useBitrixPreview(title, description, setTitle, setDescription)

  const submit = () =>
    run(async () => {
      if (!title.trim()) {
        toast('Нужен заголовок задачи', 'error')
        return
      }
      try {
        const body: Record<string, unknown> = {
          title: title.trim(),
          description: description.trim(),
          priority,
        }
        if (dirs.length) body.direction_ids = dirs
        if (assignee) body.assignee_agent_id = assignee
        if (mode) body.mode = mode
        if (acceptance.trim()) body.acceptance = acceptance.trim()
        // файлы — до создания задачи: агент увидит их с первого хода
        const ids: number[] = []
        for (let i = 0; i < files.length; i++) {
          setProgress((p) => Object.assign([...p], { [i]: 0 }))
          const a = await uploadFile<AttachmentInfo>(files[i]!, (x) => setProgress((p) => Object.assign([...p], { [i]: x })))
          ids.push(a.id)
          setProgress((p) => Object.assign([...p], { [i]: 1 }))
        }
        if (ids.length) body.attachments = ids
        const t = await api.post<Task>('/api/tasks', body)
        toast(`Задача ${t.key} создана${ids.length ? `, файлов: ${ids.length}` : ''}${assignee ? '' : ' — штаб разберёт'}`, 'success')
        onCreated?.(t)
        onClose()
      } catch (e) {
        setProgress([])
        toastError(e, 'Не удалось создать задачу')
      }
    })

  return (
    <Modal
      title="Новая задача"
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <span className="mr-auto hidden text-[12px] text-subtle sm:inline">
            <Kbd>⌘</Kbd> <Kbd>Enter</Kbd> — создать
          </span>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy} disabled={!title.trim()}>
            Создать задачу
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void submit()
          }
        }}
      >
        <Field label="Заголовок" htmlFor={ids.title}>
          <input
            id={ids.title}
            className="ctl text-[14px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Что нужно сделать? Или вставьте ссылку на задачу Битрикс24"
            data-autofocus
            required
          />
        </Field>
        <BitrixCard s={b24} />
        <DropOverlay show={dragging} />
        <Field
          label="Описание"
          htmlFor={ids.desc}
          hint="Контекст, ссылки, что уже известно. Поддерживается markdown. Скриншот можно вставить сюда же (⌘V)."
        >
          <textarea
            id={ids.desc}
            className="ctl min-h-[120px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Например: после переезда eBay-парсера цены в PIM обновляются с задержкой — разобраться, где теряется…"
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="lbl">Файлы</span>
            <FilePicker onFiles={addFiles} size="xs" />
            <span className="text-[12px] text-subtle">или перетащите в окно, скриншот — ⌘V</span>
          </div>
          <PendingFiles files={files} progress={progress} onRemove={(i) => setFiles((cur) => cur.filter((_, k) => k !== i))} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Приоритет" htmlFor={ids.prio}>
            <select
              id={ids.prio}
              className="ctl"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Исполнитель"
            htmlFor={ids.assignee}
            hint={assignee ? 'Задача сразу уйдёт этому агенту.' : 'Штаб разберёт задачу и разложит по направлениям.'}
          >
            <AgentSelect id={ids.assignee} value={assignee} onChange={setAssignee} emptyLabel="Штаб (авто-разбор)" />
          </Field>
          <Field
            label="Режим"
            htmlFor={ids.mode}
            hint={mode === 'pr_only' ? 'Агенты правят код в ветке oma/… и открывают PR; мерж и деплой — за человеком.' : mode === 'full' ? 'Действия на проде — только с вашим одобрением.' : 'Агенты читают код и данные; изменения — планом и патчем в итоге.'}
          >
            <select id={ids.mode} className="ctl" value={mode} onChange={(e) => setMode(e.target.value as SafetyMode | '')}>
              <option value="">Как в настройках</option>
              {(Object.keys(SAFETY_MODE_LABELS) as SafetyMode[]).map((m) => (
                <option key={m} value={m}>
                  {SAFETY_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {directions.length ? (
          <div className="flex flex-col gap-1.5">
            <span className="lbl" id={`${ids.title}-dirs`}>
              Направления <span className="font-normal tracking-normal text-subtle normal-case">— необязательно, штаб определит сам</span>
            </span>
            <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={`${ids.title}-dirs`}>
              {directions.map((d) => (
                <DirectionChip
                  key={d.id}
                  direction={d}
                  active={dirs.includes(d.id)}
                  className="h-7 px-2.5 text-[12.5px]"
                  onClick={() => setDirs((v) => (v.includes(d.id) ? v.filter((x) => x !== d.id) : [...v, d.id]))}
                />
              ))}
            </div>
          </div>
        ) : null}
        {showAcceptance ? (
          <Field label="Критерии приёмки" htmlFor={ids.acc} hint="Как понять, что задача сделана. Проверяющий сверит результат с ними.">
            <textarea
              id={ids.acc}
              className="ctl"
              value={acceptance}
              onChange={(e) => setAcceptance(e.target.value)}
              placeholder={'- [ ] цены обновляются не позже 15 минут\n- [ ] алерт при задержке'}
            />
          </Field>
        ) : (
          <div>
            <Button size="sm" variant="ghost" icon="plus" onClick={() => setShowAcceptance(true)}>
              Критерии приёмки
            </Button>
          </div>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}
